/* Adapter around the app's own form and live gallery; never submits cart data. */
(() => {
  if (customElements.get('personalization-assistant')) return;
  const visible = element => !!element?.getClientRects().length && !element.closest('[hidden], [aria-hidden="true"]');
  class PersonalizationAssistant extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.scope = this.closest('.shopify-section') || document;
      this.media = this.scope.querySelector('.product-media-wrapper');
      this.controls = this.querySelector('.product-live-preview-controls');
      this.hideButton = this.querySelector('[data-live-hide]');
      this.showButton = this.querySelector('[data-live-show]');
      this.bar = this.querySelector('[data-live-bar]');
      this.zoom = this.querySelector('[data-live-zoom]');
      this.zoomIn = this.querySelector('[data-live-zoom-in]');
      this.zoomOut = this.querySelector('[data-live-zoom-out]');
      this.size = PersonalizationAssistant.sizes.includes(this.dataset.liveSize) ? PersonalizationAssistant.sizes.indexOf(this.dataset.liveSize) : 1;
      this.ownErrors = new Map();
      this.events = new AbortController();
      const signal = this.events.signal;
      this.scope.addEventListener('click', event => this.previewClick(event), {capture: true, signal});
      this.scope.addEventListener('input', event => this.clearError(event.target.closest('.tee-field')), {signal});
      this.scope.addEventListener('change', event => this.clearError(event.target.closest('.tee-field')), {signal});
      this.hideButton.addEventListener('click', () => { this.collapsed = true; this.syncDock(); this.showButton.focus(); }, {signal});
      this.showButton.addEventListener('click', () => { this.collapsed = false; this.syncDock(); this.hideButton.focus(); }, {signal});
      this.zoomIn?.addEventListener('click', () => this.resize(1), {signal});
      this.zoomOut?.addEventListener('click', () => this.resize(-1), {signal});
      window.addEventListener('scroll', () => this.queue(), {passive: true, signal});
      window.addEventListener('resize', () => { this.undock(); this.queue(); }, {signal});
      window.visualViewport?.addEventListener('resize', () => this.queue(), {signal});
      this.observer = new MutationObserver(() => this.queue());
      this.observer.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-invalid']});
      this.queue();
    }
    disconnectedCallback() {
      this.events?.abort(); this.events = null;
      this.observer?.disconnect(); cancelAnimationFrame(this.frame);
      this.undock();
    }
    queue() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.refineModals();
        this.syncDock();
        if (this.pendingValidation > Date.now()) this.focusAppError();
      });
    }
    missingField() {
      for (const field of this.scope.querySelectorAll('.tee-field')) {
        if (!visible(field) || ![...field.querySelectorAll('.tee-asterisk')].some(marker => marker.closest('.tee-field') === field)) continue;
        const text = field.querySelector('input[type="text"], input[type="email"], textarea');
        if (text && visible(text) && !text.disabled && !text.value.trim()) return {field, input: text};
        const select = field.querySelector('select');
        if (select && visible(select) && !select.disabled && !select.value) return {field, input: select};
        const photo = field.querySelector('input[type="file"]');
        const placeholder = field.querySelector('.tee-photo-placeholder');
        // A restored/processed photo can leave the file input empty; the visible
        // app placeholder distinguishes it from a genuinely missing upload.
        if (photo && !photo.disabled && visible(placeholder) && !photo.files?.length && !field.querySelector('.tee-photo-head img, .tee-photo-preview img')) {
          return {field, input: field.querySelector('.tee-photo-action [tabindex="0"]') || field};
        }
        const radios = [...field.querySelectorAll('input[type="radio"]')].filter(input => !input.disabled);
        if (radios.length && !radios.some(input => input.checked) && !field.querySelector('.tee-clipart.active, .tee-radio.active')) return {field, input: field};
        const checks = [...field.querySelectorAll('input[type="checkbox"]')].filter(input => !input.disabled);
        if (checks.length && !checks.some(input => input.checked)) return {field, input: checks[0]};
      }
      return null;
    }
    previewClick(event) {
      if (this.dataset.validation === 'false') return;
      if (!event.target.closest('.tee-btn--preview, #customily-preview-button, .button-preview')) return;
      this.didFocus = false;
      // Only block a clearly empty, visible, required field. Processing and
      // rendering a provided upload remain entirely with the app.
      const missing = event.target.closest('.tee-btn--preview') && this.missingField();
      if (missing) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.showError(missing.field, missing.input);
        this.focusField(missing.field, missing.input);
      } else {
        this.pendingValidation = Date.now() + 1800;
        this.queue();
      }
    }
    showError(field, input) {
      if (this.ownErrors.has(field)) return;
      const message = document.createElement('p');
      message.className = 'mangofy-personalization-error';
      message.id = `personalization-error-${this.ownErrors.size}-${Date.now()}`;
      message.textContent = this.dataset.requiredMessage;
      message.setAttribute('role', 'alert');
      field.append(message);
      const record = {message, input, invalid: input.getAttribute('aria-invalid'), described: input.getAttribute('aria-describedby')};
      this.ownErrors.set(field, record);
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', [record.described, message.id].filter(Boolean).join(' '));
    }
    clearError(field) {
      const record = this.ownErrors.get(field);
      if (!record) return;
      record.message.remove();
      for (const [name, value] of [['aria-invalid', record.invalid], ['aria-describedby', record.described]]) {
        if (value === null) record.input.removeAttribute(name); else record.input.setAttribute(name, value);
      }
      this.ownErrors.delete(field);
    }
    focusField(field, input) {
      this.didFocus = true;
      // Hide the floating gallery so the missing field and keyboard have space.
      this.collapsed = true; this.syncDock();
      field.scrollIntoView({block: 'center', behavior: 'auto'});
      if (!input.matches('input, select, textarea, button, [tabindex]')) input.setAttribute('tabindex', '-1');
      input.focus({preventScroll: true});
    }
    focusAppError() {
      if (this.didFocus) return;
      const errors = this.scope.querySelectorAll('.tee-field--error, .tee-field.has-error, .tee-field .tee-error, #customily-options .customily-required-error, #customily-options [aria-invalid="true"], .tee-customization-wrapper [aria-invalid="true"]');
      const error = [...errors].find(visible);
      if (!error) return;
      const field = error.closest('.tee-field, .customily_option') || error;
      const input = field.querySelector('input:not([type="hidden"]):not([type="radio"]), select, textarea, button, [tabindex="0"]') || field;
      this.focusField(field, input);
    }
    refineModals() {
      if (this.dataset.refineModal === 'false') return;
      for (const dialog of document.querySelectorAll('.tee-dialog-gallery:not([data-preview-refined])')) {
        const inner = dialog.querySelector('.tee-dialog-inner');
        const close = dialog.querySelector('.tee-dialog-close');
        if (!inner || !close || !dialog.querySelector('.tee-dcontent-footer .tee-btn--atc')) continue;
        inner.prepend(this.querySelector('[data-preview-reminder]').content.cloneNode(true));
        close.setAttribute('role', 'button'); close.setAttribute('tabindex', '0'); close.setAttribute('aria-label', 'Close preview');
        close.addEventListener('keydown', event => {
          if (['Enter', ' '].includes(event.key)) { event.preventDefault(); close.click(); }
        }, {signal: this.events.signal});
        // Hide the redundant footer link only after confirming a usable X exists.
        dialog.setAttribute('data-preview-refined', '');
      }
    }
    resize(step) {
      this.size = Math.min(PersonalizationAssistant.sizes.length - 1, Math.max(0, (this.size ?? 1) + step));
      this.syncDock();
    }
    undock() {
      this.media?.removeAttribute('data-live-preview-active');
      this.media?.style.removeProperty('--live-original-height');
      document.documentElement?.style.removeProperty('scroll-padding-top');
      this.controls.hidden = true;
    }
    syncDock() {
      if (!this.media || !window.matchMedia('(max-width: 749px)').matches || this.dataset.livePreview === 'false') { this.undock(); return; }
      const source = this.media.querySelector(':scope > .tee-gallery-content');
      const form = this.scope.querySelector('.tee-customization-wrapper');
      const modal = document.querySelector('.tee-dialog[aria-expanded="true"], .tee-dialog .vm--modal, search-discovery[open], html.side-cart-open');
      const top = Math.max(0, document.querySelector('.site-header')?.getBoundingClientRect().bottom || 0);
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      // Full-width bar: the preview takes a share of the visible height (customers can zoom) and never exceeds the screen width.
      const height = Math.round(Math.max(140, Math.min(window.innerWidth - 32, (viewportHeight - top) * PersonalizationAssistant.shares[this.size ?? 1])));
      const barHeight = this.collapsed ? 0 : height + 16;
      if (!source || !form || modal || this.media.getBoundingClientRect().bottom > 0 || form.getBoundingClientRect().bottom < top + barHeight + 40 || form.getBoundingClientRect().top > viewportHeight) { this.undock(); return; }
      if (!this.media.hasAttribute('data-live-preview-active') && !this.collapsed) {
        const wrapper = this.media.getBoundingClientRect();
        const rect = source.getBoundingClientRect();
        this.sourceWidth = rect.width;
        this.sourceHeight = source.querySelector?.('.tee-gallery__stage')?.getBoundingClientRect().height || rect.width;
        if (!this.sourceWidth) return;
        this.media.style.setProperty('--live-original-height', `${wrapper.height}px`);
        this.media.style.setProperty('--live-source-width', `${this.sourceWidth}px`);
        this.media.setAttribute('data-live-preview-active', '');
      }
      const scale = Math.min(height / (this.sourceHeight || this.sourceWidth || height), (window.innerWidth - 24) / (this.sourceWidth || window.innerWidth));
      for (const node of [this.media, this.controls]) {
        node.style.setProperty('--live-preview-top', `${top}px`);
        node.style.setProperty('--live-preview-height', `${barHeight}px`);
      }
      this.media.style.setProperty('--live-preview-scale', String(scale));
      // Focused fields (and their labels) scroll into view below the bar instead of under it.
      document.documentElement?.style.setProperty('scroll-padding-top', `${top + barHeight + 48}px`);
      if (this.collapsed) this.media.removeAttribute('data-live-preview-active');
      this.controls.hidden = false;
      this.hideButton.hidden = !!this.collapsed;
      this.showButton.hidden = !this.collapsed;
      if (this.bar) this.bar.hidden = !!this.collapsed;
      if (this.zoom) this.zoom.hidden = !!this.collapsed;
      if (this.zoomIn) this.zoomIn.disabled = this.size >= PersonalizationAssistant.sizes.length - 1;
      if (this.zoomOut) this.zoomOut.disabled = this.size <= 0;
    }
  }
  PersonalizationAssistant.sizes = ['small', 'medium', 'large'];
  PersonalizationAssistant.shares = [.27, .36, .5];
  customElements.define('personalization-assistant', PersonalizationAssistant);
})();
